import { ChevronRight } from "@mui/icons-material";
import { Button, Grid, MenuItem, TextField, Typography } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import CloseArrowTooltip from "../../../components/CloseArrowTooltip";
import {
  ProjectContext,
  ProjectDispatchContext,
} from "../../../contexts/ProjectContext";
import {
  convertProjectContextToProject,
  convertProjectToProjectContext,
} from "../../../models/Project";
import { defaultSource } from "../../../models/Source";
import { useFetch } from "../../../services/hooks/useFetch";
import { apiPrefix, deviceApiPrefix, sourceAsText } from "../../../utils/main";

export default function IncrementalSettingsPage() {
  const [editMode, setEditMode] = useState(false);

  const project = useContext(ProjectContext)!;
  const { data: projectSource = defaultSource, fetch: getProjectSource } =
    useFetch<Source>();
  const dispatch = useContext(ProjectDispatchContext);
  const { update: updateProject } = useFetch<
    undefined,
    ProjectRequest,
    Project
  >();

  const { data: parentProject, fetch: getCopiedProject } = useFetch<Project>();
  const { data: parentProjectSource, fetch: getParentProjectSource } =
    useFetch<Source>();

  const { data: highlightDateOptions, fetch: getHighlightDateOptions } =
    useFetch<Date[], undefined, string[]>();
  const [projectHighlightDate, setProjectHighlightDate] = useState<Date | null>(
    project.highlightDate ? project.highlightDate : null,
  );

  const [updateReason, setUpdateReason] = useState("");

  const [displayErrors, setDisplayErrors] = useState(false);

  const handleUpdates = function () {
    if (updateReason !== "") {
      project.highlightDate = projectHighlightDate;

      void updateProject({
        path: `${apiPrefix}/projects/${project.id}`,
        body: {
          reason: updateReason,
          project: convertProjectContextToProject(project),
        },
        responseManipulationFunction: (data) => {
          convertProjectToProjectContext(data, dispatch);
          return;
        },
        postResponseAction: () => {
          setEditMode(false);
          setDisplayErrors(false);
          setUpdateReason("");
        },
      });
    } else {
      setDisplayErrors(true);
    }
  };

  useEffect(() => {
    if (project) {
      void getProjectSource(`${deviceApiPrefix}/sources/${project.sourceId}`);
      void getHighlightDateOptions(
        `${apiPrefix}/projects/${project.id}/highlightDateOptions`,
        {
          responseManipulationFunction: (data: string[]) => {
            return data
              .map((d) => new Date(d))
              .sort((a, b) => a.getTime() - b.getTime());
          },
        },
      );
      if (project.copiedFromProjectId) {
        void getCopiedProject(
          `${apiPrefix}/projects/${project.copiedFromProjectId}`,
        );
      }
    }
  }, [project, getCopiedProject, getProjectSource, getHighlightDateOptions]);

  useEffect(() => {
    if (parentProject) {
      void getParentProjectSource(
        `${deviceApiPrefix}/sources/${parentProject.sourceId}`,
      );
    }
  }, [getParentProjectSource, parentProject]);

  return (
    <Grid container direction={"column"} className="company-settings-item">
      <div className="company-settings-item">
        <Grid>
          <Typography variant="h4">Incremental Settings</Typography>
        </Grid>
        <br />
        <Grid>
          <Typography variant="body2">
            The data source, dates, base project and highlight configuration for
            this project. The highlight date can be modified at any time after
            the project is created.
          </Typography>
        </Grid>
        <br />
        <Grid>
          <Typography variant="body2">* Required fields</Typography>
        </Grid>
        <br />
        <Grid container direction={"row"} columnSpacing={2} rowSpacing={3}>
          <Grid container size={6}>
            <Grid size={12}>
              <Typography variant="h5">{project.name}</Typography>
            </Grid>
            <Grid size={12}>
              <Typography variant="caption">
                Project Copy/Created Date
              </Typography>
              <Typography variant="body1" sx={{ width: "50ch" }}>
                {new Date(project.createdAt!).toDateString()}
              </Typography>
            </Grid>
            <Grid size={12}>
              <Typography variant="caption">Data Source</Typography>
              <Typography variant="body1" sx={{ width: "50ch" }}>
                {sourceAsText(projectSource)}
              </Typography>
            </Grid>
            <Grid size={12}>
              <Typography variant="body1">Procedure Date Range</Typography>
              <Grid container spacing={2}>
                <Grid>
                  <Typography variant="caption">Start Date</Typography>
                  <Typography variant="body1">
                    {project.startDate ? project.startDate.toDateString() : ""}
                  </Typography>
                </Grid>
                <Grid alignContent={"end"}>
                  <ChevronRight />
                </Grid>
                <Grid>
                  <Typography variant="caption">End Date</Typography>
                  <Typography variant="body1">
                    {project.endDate ? project.endDate.toDateString() : ""}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid size={12}>
              <Typography variant="body1">Project Data Window</Typography>
              <Grid container spacing={2}>
                <Grid>
                  <Typography variant="caption">
                    Days Pre Index Procedure
                  </Typography>
                  <Typography variant="body1">
                    {project.defaultPreIndexDays}
                  </Typography>
                </Grid>
                <Grid alignContent={"end"}>
                  <ChevronRight />
                </Grid>
                <Grid>
                  <Typography variant="caption">
                    Days Post Index Procedure
                  </Typography>
                  <Typography variant="body1">
                    {project.defaultPostIndexDays}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          {parentProject && (
            <Grid container size={6}>
              <Grid size={12}>
                <Typography variant="h5">{parentProject.name}</Typography>
              </Grid>
              <Grid size={12}>
                <Typography variant="caption">
                  Project Copy/Created Date
                </Typography>
                <Typography variant="body1" sx={{ width: "50ch" }}>
                  {new Date(parentProject.createdAt!).toDateString()}
                </Typography>
              </Grid>
              <Grid size={12}>
                <Typography variant="caption">Data Source</Typography>
                <Typography variant="body1" sx={{ width: "50ch" }}>
                  {parentProjectSource && sourceAsText(parentProjectSource)}
                </Typography>
              </Grid>
              <Grid size={12}>
                <Typography variant="body1">Procedure Date Range</Typography>
                <Grid container spacing={2}>
                  <Grid>
                    <Typography variant="caption">Start Date</Typography>
                    <Typography variant="body1">
                      {new Date(parentProject.startDate!).toDateString()}
                    </Typography>
                  </Grid>
                  <Grid alignContent={"end"}>
                    <ChevronRight />
                  </Grid>
                  <Grid>
                    <Typography variant="caption">End Date</Typography>
                    <Typography variant="body1">
                      {new Date(parentProject.endDate!).toDateString()}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid size={12}>
                <Typography variant="body1">Project Data Window</Typography>
                <Grid container spacing={2}>
                  <Grid>
                    <Typography variant="caption">
                      Days Pre Index Procedure
                    </Typography>
                    <Typography variant="body1">
                      {parentProject.defaultPreIndexDays}
                    </Typography>
                  </Grid>
                  <Grid alignContent={"end"}>
                    <ChevronRight />
                  </Grid>
                  <Grid>
                    <Typography variant="caption">
                      Days Post Index Procedure
                    </Typography>
                    <Typography variant="body1">
                      {parentProject.defaultPostIndexDays}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
        <br />
        {editMode ? (
          <Grid>
            <TextField
              size="small"
              label="Highlight Data After"
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
              helperText="Select the start date that should be used for highlighting data."
              select
              required
              sx={{ width: "50ch" }}
              value={projectHighlightDate?.toISOString() ?? "None"}
              onChange={(event) => {
                const value = event.target.value;
                const newDate = value === "None" ? null : new Date(value);
                if (newDate && !isNaN(Number(newDate))) {
                  setProjectHighlightDate(newDate);
                } else {
                  setProjectHighlightDate(null);
                }
              }}
            >
              <MenuItem value={"None"}>No Highlighting</MenuItem>
              {highlightDateOptions && highlightDateOptions.length > 0 ? (
                highlightDateOptions.map((option) => (
                  <MenuItem
                    key={option.toISOString()}
                    value={option.toISOString()}
                  >
                    {option.toString()}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value={projectHighlightDate?.toISOString()}>
                  {projectHighlightDate?.toString()}
                </MenuItem>
              )}
            </TextField>
          </Grid>
        ) : (
          <Grid>
            <Typography variant="caption">Highlight Data After *</Typography>
            <Typography variant="body1" sx={{ width: "50ch" }}>
              {(project.highlightDate && project.highlightDate.toString()) ??
                "No Highlighting"}
            </Typography>
          </Grid>
        )}

        {editMode && (
          <div>
            <br />
            <Grid>
              <TextField
                required
                label="Note/Reason for Updating the Project Details"
                sx={{ width: "50ch" }}
                value={updateReason}
                error={displayErrors && updateReason === ""}
                helperText={
                  displayErrors && updateReason === ""
                    ? "This field is required"
                    : ""
                }
                onChange={(event) => {
                  setUpdateReason(event.target.value);
                }}
              ></TextField>
            </Grid>
          </div>
        )}
        <br />
        <Grid container justifyContent={"right"}>
          {(editMode && (
            <>
              <Button
                variant="outlined"
                onClick={() => {
                  setEditMode(false);
                  setDisplayErrors(false);
                  setUpdateReason("");
                  setProjectHighlightDate(
                    project.highlightDate ? project.highlightDate : null,
                  );
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                className="update-button"
                sx={{ marginLeft: "5px" }}
                onClick={handleUpdates}
              >
                Update
              </Button>
            </>
          )) || (
            <>
              <CloseArrowTooltip
                title={project.isLocked ? "This project is locked." : ""}
              >
                <span>
                  <Button
                    variant="contained"
                    onClick={() => setEditMode(true)}
                    disabled={project.isLocked}
                  >
                    Edit
                  </Button>
                </span>
              </CloseArrowTooltip>
            </>
          )}
        </Grid>
      </div>
    </Grid>
  );
}
